import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, For } from 'solid-js'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const ChipPage: Component = () => {
  const [activeFilters, setActiveFilters] = createSignal(['Diseño', 'Frontend', 'SolidJS'])

  const removeFilter = (filter: string) => {
    setActiveFilters(activeFilters().filter(f => f !== filter))
  }

  return (
    <>
      <Title>Chip Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Chip</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Elementos compactos que representan atributos, entidades o acciones. Los chips permiten a los usuarios introducir información, realizar selecciones, filtrar contenido o activar acciones de
            forma visual y táctil.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes y Colores */}
          <Card>
            <Card.Header
              title="Estética y Semántica"
              subtitle="Tres lenguajes visuales y una paleta completa para categorizar cualquier tipo de dato."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest px-1 border-l-2 border-primary-500/30 ml-1">Variante filled (Default)</h4>
                  <div class="flex flex-wrap items-center gap-3 p-6 bg-surface-muted rounded-xl border border-border">
                    <Chip color="primary">Primary</Chip>
                    <Chip color="secondary">Secondary</Chip>
                    <Chip color="success">Success</Chip>
                    <Chip color="danger">Danger</Chip>
                    <Chip color="warning">Warning</Chip>
                    <Chip color="info">Info</Chip>
                    <Chip color="default">Default</Chip>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div class="space-y-4">
                    <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Variante outlined</h4>
                    <div class="flex flex-wrap gap-2 p-6 bg-surface border border-border rounded-xl shadow-sm">
                      <Chip variant="outlined" color="primary">
                        Borde Colorido
                      </Chip>
                      <Chip variant="outlined" color="success">
                        Validado
                      </Chip>
                    </div>
                  </div>
                  <div class="space-y-4">
                    <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Variante soft</h4>
                    <div class="flex flex-wrap gap-2 p-6 bg-surface-muted border border-border rounded-xl">
                      <Chip variant="soft" color="info">
                        Fondo Suave
                      </Chip>
                      <Chip variant="soft" color="warning">
                        Advertencia sutil
                      </Chip>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños y Modificadores */}
          <Card>
            <Card.Header
              title="Escalabilidad y Multimedia"
              subtitle="Optimiza el espacio con diferentes tamaños e integra avatares o iconos para mayor contexto."
              icon={<Icon icon="material-symbols-light:maximize" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="p-8 bg-surface-muted rounded-xl border border-border space-y-6">
                  <h4 class="font-bold text-foreground">Dimensiones (Size)</h4>
                  <div class="flex items-end gap-4">
                    <div class="text-center space-y-2">
                      <Chip size="xs" color="primary">
                        Extra
                      </Chip>
                      <p class="text-[10px] text-foreground-subtle font-bold">xs</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Chip size="sm" color="primary">
                        Pequeño
                      </Chip>
                      <p class="text-[10px] text-foreground-subtle font-bold">sm</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Chip size="md" color="primary">
                        Mediano
                      </Chip>
                      <p class="text-[10px] text-foreground-subtle font-bold">md</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Chip size="lg" color="primary">
                        Grande
                      </Chip>
                      <p class="text-[10px] text-foreground-subtle font-bold">lg</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Chip size="xl" color="primary">
                        Extra
                      </Chip>
                      <p class="text-[10px] text-foreground-subtle font-bold">xl</p>
                    </div>
                  </div>
                </div>

                <div class="p-8 bg-surface-muted rounded-xl border border-border space-y-6">
                  <h4 class="font-bold text-foreground">Multimedia Integrada</h4>
                  <div class="flex flex-wrap gap-4">
                    <Chip color="primary" icon="material-symbols-light:star-outline">
                      Destacado
                    </Chip>
                    <Chip color="secondary" avatar="https://i.pravatar.cc/150?u=chip1">
                      Alex G.
                    </Chip>
                    <Chip color="info" variant="soft" icon="material-symbols-light:settings-outline" removable>
                      Ajustes
                    </Chip>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Ejemplo Real: Etiquetas de Blog / Filtros */}
          <Card>
            <Card.Header
              title="Caso Práctico: Filtros Activos"
              subtitle="Uso de chips removibles para gestionar selecciones complejas en tiempo real."
              icon={<Icon icon="material-symbols-light:filter-list" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 flex flex-col items-center justify-center bg-surface rounded-2xl border border-border shadow-xl overflow-hidden relative">
                <div class="flex flex-wrap justify-center gap-3 max-w-lg relative z-10">
                  <For each={activeFilters()}>
                    {tag => (
                      <Chip color="primary" variant="soft" removable onRemove={() => removeFilter(tag)} class="hover:scale-105 transition-transform">
                        {tag}
                      </Chip>
                    )}
                  </For>
                  {activeFilters().length === 0 && <p class="text-foreground-subtle italic text-sm py-4">No hay filtros activos. Recarga la página para restaurar.</p>}
                </div>
                <div class="mt-8 pt-6 border-t border-border w-full text-center">
                  <p class="text-xs text-foreground-subtle">
                    Total de resultados: <span class="text-foreground font-bold">{activeFilters().length * 12} items</span>
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Manual técnico de las propiedades disponibles para el componente Chip."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">color</td>
                      <td class="px-6 py-4">MainColorsType</td>
                      <td class="px-6 py-4 font-mono">'default'</td>
                      <td class="px-6 py-4 italic text-balance">Paleta de color temática aplicada al fondo o borde.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">variant</td>
                      <td class="px-6 py-4">'filled' | 'outlined' | 'soft'</td>
                      <td class="px-6 py-4 font-mono">'filled'</td>
                      <td class="px-6 py-4 italic text-balance">Estilo visual del contenedor.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4">Dimensiones físicas del chip.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">icon</td>
                      <td class="px-6 py-4">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Nombre del icono (Solar) a mostrar a la izquierda.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">avatar</td>
                      <td class="px-6 py-4">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">URL de imagen para mostrar un avatar circular a la izquierda.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-danger-500">removable</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Habilita el botón de cierre/eliminación.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onRemove</td>
                      <td class="px-6 py-4">() =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Evento disparado al hacer clic en el botón de eliminar.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ChipPage
